import {
    Component,
    ElementRef,
    Inject,
    OnInit,
    Optional,
    ViewChild,
    ViewEncapsulation,
    PLATFORM_ID,
    afterNextRender,
    Injector,
    DOCUMENT,
} from '@angular/core';
import { isPlatformBrowser, isPlatformServer, ViewportScroller } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { NavigationStart, Router } from '@angular/router';
import get from 'lodash-es/get';

import { REQUEST } from '../express.tokens';
import { Request } from 'express';

import { appIcons } from './app-icons';
import { CategoryService } from 'lib/services/category/category.service';
import { contains, wait, getHeight } from 'lib/tools';
import { InfiniteScrollService } from 'lib/services/infinite-scroll.service';
import { RouterService } from 'lib/services/router.service';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import { MarketoService } from 'lib/services/marketo.service';

const scriptName = 'app.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class AppComponent implements OnInit {
    @ViewChild('infiniteScrollGlobalContainer')
    infiniteScrollGlobalContainer: ElementRef;
    resourcePages: string[] = [
        'blog',
        'case-studies',
        'documents',
        'eBooks',
        'News-Events',
        'Reference-Architecture',
        'Supported-Software',
        'Whitepapers',
    ];
    isResourcePage: boolean = false;
    isBrowser: boolean;

    // Navigation context tracking for smart scroll behavior
    private previousUrl: string = '';
    private navigationTrigger: 'imperative' | 'popstate' | 'hashchange' = 'imperative';

    constructor(
        @Optional() @Inject(REQUEST) protected req: Request,
        @Inject(PLATFORM_ID) private platformId: string,
        @Inject(DOCUMENT) private document: Document,
        @Inject('environment') private environment: any,
        private categoryService: CategoryService,
        private domSanitizer: DomSanitizer,
        private infiniteScrollService: InfiniteScrollService,
        private marketoService: MarketoService,
        private matIconRegistry: MatIconRegistry,
        private router: Router,
        private routerService: RouterService,
        private viewportScroller: ViewportScroller,
        private injector: Injector
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    ngOnInit() {
        try {
            this.initIcons();
            this.initInfiniteScroll();
            this.initNavigationTracking();
            this.initRouteListener();
            if (!this.isBrowser) {
                return;
            }
            getHeight();
            addEventListener('resize', () => {
                getHeight();
            });
        } catch (err) {
            console.error(...new ExxComError(198734, scriptName, err).stamp());
        }
    }

    private initIcons() {
        if (isPlatformServer(this.platformId)) {
            return;
        }
        const version = this.environment.version || '1.0.0';
        appIcons.forEach((iconName: any) => {
            const path = `/assets/icons/${iconName}.svg?v=${version}`;
            const url = this.domSanitizer.bypassSecurityTrustResourceUrl(path);
            this.matIconRegistry.addSvgIcon(iconName, url);
        });
    }

    /**
     * Tracks navigation trigger (imperative vs popstate/back button) for smart scroll decisions
     */
    private initNavigationTracking() {
        try {
            this.router.events.subscribe((event) => {
                if (event instanceof NavigationStart) {
                    this.navigationTrigger = event.navigationTrigger;
                }
            });
        } catch (err) {
            console.error(...new ExxComError(997838, scriptName, err).stamp());
        }
    }

    private initRouteListener() {
        try {
            this.routerService.onRouteChange('appComponent', (event: any) => {
                this.isResourcePage = this.isResourcePages();
                if (!this.isBrowser) {
                    return;
                }

                // Smart scroll decision based on navigation context
                const isCurrentCategory = contains(event.url, this.categoryService.paths);
                const isPreviousCategory = contains(this.previousUrl, this.categoryService.paths);
                const isDifferentCategory = this.routerService.url.component !== this.routerService.url.componentPrev;
                let shouldScrollToTop = false;

                // Decision tree for scroll behavior
                if (this.navigationTrigger === 'popstate') {
                    // Back/forward button - never scroll (preserve user's expected position)
                    shouldScrollToTop = false;
                } else if (isCurrentCategory && isPreviousCategory && !isDifferentCategory) {
                    // Category-to-category navigation - preserve scroll for browsing
                    shouldScrollToTop = false;
                } else {
                    // All other cases - scroll to top
                    // Includes: homepage→category, product→category, blog→page, etc.
                    shouldScrollToTop = true;
                }

                if (shouldScrollToTop) {
                    // Multi-strategy scroll handling to fix race conditions with lazy-loaded content

                    // Strategy 1: Immediate scroll (handles fast-loading pages)
                    this.viewportScroller.scrollToPosition([0, 0]);

                    // Strategy 2: After next render cycle (handles component initialization)
                    afterNextRender(
                        () => {
                            this.viewportScroller.scrollToPosition([0, 0]);
                        },
                        { injector: this.injector }
                    );

                    // Strategy 3: Delayed scroll (handles async content and lazy images)
                    setTimeout(() => {
                        this.viewportScroller.scrollToPosition([0, 0]);
                    }, 100);

                    // Strategy 4: After lazy images load (handles late layout shifts)
                    this.ensureScrollAfterImagesLoad();
                }

                // Update navigation history
                this.previousUrl = event.url;

                this.marketoService.init();
                if (!this.routerService.url.current.includes('configurator')) {
                    this.marketoService.initLoaded();
                }
            });
        } catch (err) {
            console.error(...new ExxComError(997839, scriptName, err).stamp());
        }
    }

    /**
     * Ensures scroll to top after all lazy-loaded images complete loading
     * Handles race condition where images cause layout shifts after initial scroll
     */
    private ensureScrollAfterImagesLoad() {
        try {
            if (!this.isBrowser) {
                return;
            }

            // Stop correcting once the user starts moving through the page: lazy images keep loading
            // as they scroll or Tab down, and each late scroll-to-top threw them back to the top
            let userHasMoved = false;
            const markMoved = () => (userHasMoved = true);
            ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((type) =>
                window.addEventListener(type, markMoved, { once: true, passive: true, capture: true })
            );
            const scrollToTopUnlessMoved = () => {
                if (!userHasMoved) {
                    this.viewportScroller.scrollToPosition([0, 0]);
                }
            };

            // Small delay to allow DOM to settle before querying images
            setTimeout(() => {
                const lazyImages = document.querySelectorAll('img[loading="lazy"]');
                if (lazyImages.length === 0) {
                    return;
                }

                let loadedCount = 0;
                const totalImages = lazyImages.length;

                lazyImages.forEach((img: HTMLImageElement) => {
                    if (img.complete) {
                        loadedCount++;
                        // If all images already loaded, scroll immediately
                        if (loadedCount === totalImages) {
                            scrollToTopUnlessMoved();
                        }
                    } else {
                        img.addEventListener(
                            'load',
                            () => {
                                loadedCount++;
                                // Scroll after each image loads to handle incremental layout shifts
                                scrollToTopUnlessMoved();
                            },
                            { once: true }
                        );
                        // Also handle image load errors
                        img.addEventListener(
                            'error',
                            () => {
                                loadedCount++;
                            },
                            { once: true }
                        );
                    }
                });
            }, 50);
        } catch (err) {
            console.error(...new ExxComError(997840, scriptName, err).stamp());
        }
    }
    isResourcePages() {
        if (this.isBrowser) {
            let didFindResourcePage = false;
            this.resourcePages.forEach((page) => {
                if (contains(window.location.pathname, page)) {
                    didFindResourcePage = true;
                }
            });
            return didFindResourcePage;
        } else {
            let didFindResourcePage = false;
            this.resourcePages.forEach((page) => {
                if (contains(get(this.req, 'originalUrl'), page)) {
                    didFindResourcePage = true;
                }
            });
            return didFindResourcePage;
        }
    }

    async initInfiniteScroll() {
        if (!this.isBrowser) {
            return;
        }
        await wait('10ms');
        this.infiniteScrollService.initGlobalContainer(this.infiniteScrollGlobalContainer.nativeElement);
    }

    // Focus the main region directly; following the #main-content hash would go through the router
    skipToMain(event: Event) {
        event.preventDefault();
        document.getElementById('main-content')?.focus();
    }
}
